import { Input } from '@workspace/ui/components/input';
import { forwardRef, type InputHTMLAttributes, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { ScrollArea } from '@workspace/ui/components/scroll-area';
import { SelectItem } from './select-item';
import { Icon } from '../icon/icon';
import { cn } from '@workspace/ui/lib/utils';

interface SelectFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  items?: { value: string; label: string }[];
  readonly?: boolean;
  wrapperClass?: string;
  onSelect?: (value: unknown) => unknown;
}

const SelectField = forwardRef<HTMLInputElement, SelectFieldProps>(
  ({ items, readonly, wrapperClass, onSelect, ...props }, ref) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState({
      value: '',
      label: '',
    });

    return (
      <div className={cn('relative', wrapperClass)}>
        <Popover
          open={open}
          onOpenChange={(open) => {
            if (props.disabled) return;

            setOpen(open);
          }}
          modal={true}
        >
          <PopoverTrigger className='w-full'>
            <Input
              ref={ref}
              readOnly={readonly}
              style={{ cursor: readonly ? 'pointer' : 'text' }}
              {...props}
              className={cn(
                `w-full h-10 pl-4 pr-9 bg-[#F5F5F5] border-none outline-none text-[#101010] text-sm font-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-1 focus-visible:outline-[#101010] focus-visible:outline-offset-0 placeholder:text-[#BFBFBF] caret-[#3C78F4] ${props.value?.toString().includes('font') && props.value}`,
                props.className,
              )}
              value={items?.find((item) => item.value === props.value)?.label || selected.label}
              onChange={(e) => {
                e.preventDefault();

                if (readonly) return;

                setSelected({
                  label: items?.find((item) => item.value === e.target.value)?.label || '',
                  value: e.target.value,
                });

                onSelect?.(e.target.value);
              }}
            />
            <Icon
              name='Down'
              size={16}
              color='#101010'
              wrapperStyle={{ position: 'absolute', top: '12px', right: '12px' }}
            />
          </PopoverTrigger>
          <PopoverContent
            onOpenAutoFocus={(e) => e.preventDefault()}
            style={{ zIndex: 50 }}
            className='w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popover-content-available-height)px] p-0 mt-4 border-[var(--border)]'
          >
            <ScrollArea className='w-full p-2 rounded-md bg-white shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] border-0'>
              <div className='w-full max-h-60'>
              {items?.map((item) => (
                    <SelectItem
                      key={item.value}
                      value={item.value}
                      label={item.label}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected({
                          label: item.label,
                          value: item.value,
                        });
                        onSelect?.(item.value);
                        setOpen(false);
                      }}
                    />
                  ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);
SelectField.displayName = 'SelectField';

export { SelectField };
